import { db } from '../firebase';
import {
  collection,
  query,
  where,
  getDocs,
  doc,
  getDoc,
  addDoc,
  updateDoc,
  onSnapshot,
  Timestamp,
  orderBy,
} from 'firebase/firestore';
import {
  ProjectScore,
  Comment,
  JudgeAssignment,
  ProjectJudgingData,
  JudgingStats,
  JudgeAssignmentStatus,
} from '../types/judging.types';
import { Project } from '../types/project.types';

export const judgingService = {
  // Get all available projects that can be judged
  subscribeToAvailableProjects(callback: (projects: Project[]) => void) {
    const projectsRef = collection(db, 'projects');
    // Query for projects that are in a judgeable state
    const q = query(
      projectsRef,
      where('status', 'in', ['submitted', 'approved']),
      orderBy('updatedAt', 'desc')
    );

    return onSnapshot(q, (snapshot) => {
      const projects = snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
        createdAt: doc.data().createdAt.toDate(),
        updatedAt: doc.data().updatedAt.toDate(),
      })) as Project[];

      callback(projects);
    });
  },

  // Get all projects with their judging status for a specific judge
  async getProjectsWithJudgingStatus(
    judgeId: string
  ): Promise<Array<Project & { judgingStatus: JudgeAssignmentStatus | null }>> {
    // Step 0: Get judge's assigned categories
    const judgeDocRef = doc(db, 'users', judgeId);
    const judgeDoc = await getDoc(judgeDocRef);

    if (!judgeDoc.exists()) {
      return [];
    }

    const assignedCategories = judgeDoc.data()?.assignedCategory || [];

    if (!Array.isArray(assignedCategories) || assignedCategories.length === 0) {
      return [];
    }

    // Step 1: Query only projects that match those categories
    const projectsRef = collection(db, 'projects');
    const projectsQuery = query(
      projectsRef,
      where('status', 'in', ['submitted', 'approved']),
      where('categories', 'array-contains-any', assignedCategories)
    );

    const projectsSnapshot = await getDocs(projectsQuery);

    const projects = projectsSnapshot.docs.map((doc) => {
      const data = doc.data();
      return {
        id: doc.id,
        ...data,
        createdAt: data.createdAt.toDate(),
        updatedAt: data.updatedAt.toDate(),
      };
    }) as Project[];

    // Step 2: Fetch scores and assignments for this judge
    const scoresRef = collection(db, 'projectScores');
    const scoresQuery = query(scoresRef, where('judgeId', '==', judgeId));
    const scoresSnapshot = await getDocs(scoresQuery);
    const scores = new Map(
      scoresSnapshot.docs.map((doc) => [
        doc.data().projectId,
        doc.data().status,
      ])
    );

    const assignmentsRef = collection(db, 'judgeAssignments');
    const assignmentsQuery = query(
      assignmentsRef,
      where('judgeId', '==', judgeId)
    );
    const assignmentsSnapshot = await getDocs(assignmentsQuery);
    const assignments = new Map(
      assignmentsSnapshot.docs.map((doc) => [
        doc.data().projectId,
        doc.data().status,
      ])
    );

    // Step 3: Merge project with status
    const result = projects.map((project) => ({
      ...project,
      judgingStatus:
        scores.get(project.id) || assignments.get(project.id) || null,
    }));
    return result;
  },

  async getApprovedProjectsByFair(fairId: string): Promise<Project[]> {
    const projectsRef = collection(db, 'projects');
    const q = query(
      projectsRef,
      where('fairId', '==', fairId),
      where('status', '==', 'approved')
    );
    const snapshot = await getDocs(q);
    return snapshot.docs.map((doc) => ({
      id: doc.id,
      ...doc.data(),
      createdAt: doc.data().createdAt.toDate(),
      updatedAt: doc.data().updatedAt.toDate(),
    })) as Project[];
  },

  async getAllApprovedProjects(): Promise<Project[]> {
    const projectsRef = collection(db, 'projects');
    const q = query(projectsRef, where('status', '==', 'approved'));
    const snapshot = await getDocs(q);

    return snapshot.docs.map((doc) => ({
      id: doc.id,
      ...doc.data(),
      createdAt: doc.data().createdAt.toDate(),
      updatedAt: doc.data().updatedAt.toDate(),
    })) as Project[];
  },

  async getProjectsByCategoryAssignments(judgeId: string): Promise<Project[]> {
    const categoriesRef = collection(db, 'categories');
    const categoriesSnapshot = await getDocs(categoriesRef);

    const assignedCategoryIds: string[] = [];
    categoriesSnapshot.forEach((doc) => {
      const data = doc.data();
      if (data.assignedJudgeIds?.includes(judgeId)) {
        assignedCategoryIds.push(doc.id);
      }
    });

    if (assignedCategoryIds.length === 0) return [];

    const projectsRef = collection(db, 'projects');
    const projectsSnapshot = await getDocs(
      query(
        projectsRef,
        where('categories', 'array-contains-any', assignedCategoryIds)
      )
    );

    return projectsSnapshot.docs.map((doc) => ({
      id: doc.id,
      ...doc.data(),
      createdAt: doc.data().createdAt.toDate(),
      updatedAt: doc.data().updatedAt.toDate(),
    })) as Project[];
  },

  async getJudgeProjectsByCategory(
    judgeId: string,
    fairId: string,
    _judgeCategoryIds: string[],
    allCategories: {
      id: string;
      linkedCategoryIds?: string[];
      assignedJudgeIds?: string[];
    }[]
  ): Promise<{
    direct: (Project & { scoreCount: number })[];
    linked: (Project & { scoreCount: number })[];
  }> {
    console.log(
      'All Categories:',
      allCategories.map((c) => ({
        id: c.id,
        assigned: c.assignedJudgeIds,
        linked: c.linkedCategoryIds,
      }))
    );

    // Step 1: Get all categories where judge is assigned
    const assignedCategoryIds = allCategories
      .filter((cat) => cat.assignedJudgeIds?.includes(judgeId))
      .map((cat) => cat.id);

    const linkedSet = new Set<string>();
    for (const cat of allCategories) {
      if (cat.assignedJudgeIds?.includes(judgeId)) {
        cat.linkedCategoryIds?.forEach((linkedId) => linkedSet.add(linkedId));
      }
    }

    // Step 2: Get all projects in the fair with overlapping categories
    const projectsRef = collection(db, 'projects');
    const q = query(
      projectsRef,
      where('fairId', '==', fairId),
      where('categories', 'array-contains-any', [
        ...assignedCategoryIds,
        ...Array.from(linkedSet),
      ])
    );
    const snapshot = await getDocs(q);

    const projects: Project[] = snapshot.docs.map((doc) => ({
      id: doc.id,
      ...doc.data(),
      createdAt: doc.data().createdAt.toDate(),
      updatedAt: doc.data().updatedAt.toDate(),
    })) as Project[];

    // Categorize into direct (assignedCategoryIds) and linked (linkedSet)
    const direct: Project[] = [];
    const linked: Project[] = [];

    for (const project of projects) {
      const catIds = project.categories || [];
      const isDirect = catIds.some((id) => assignedCategoryIds.includes(id));
      const isLinked = catIds.some((id) => linkedSet.has(id)) && !isDirect;

      if (isDirect) direct.push(project);
      else if (isLinked) linked.push(project);
    }

    // Get score count by this judge
    const scoresRef = collection(db, 'projectScores');
    const scoresQuery = query(scoresRef, where('judgeId', '==', judgeId));
    const scoresSnapshot = await getDocs(scoresQuery);

    const scoreMap = new Map<string, number>();
    scoresSnapshot.docs.forEach((doc) => {
      const projectId = doc.data().projectId;
      scoreMap.set(projectId, (scoreMap.get(projectId) || 0) + 1);
    });

    const attachScoreCount = (
      p: Project
    ): Project & { scoreCount: number } => ({
      ...p,
      scoreCount: scoreMap.get(p.id) || 0,
    });

    return {
      direct: direct.map(attachScoreCount),
      linked: linked.map(attachScoreCount),
    };
  },

  // Subscribe to a judge's assigned projects
  subscribeToJudgeAssignments(
    judgeId: string,
    callback: (assignments: JudgeAssignment[]) => void
  ) {
    const assignmentsRef = collection(db, 'judgeAssignments');
    const q = query(
      assignmentsRef,
      where('judgeId', '==', judgeId),
      orderBy('assignedAt', 'desc')
    );

    return onSnapshot(q, (snapshot) => {
      const assignments = snapshot.docs.map(
        (doc) =>
          ({
            id: doc.id,
            ...doc.data(),
            assignedAt: doc.data().assignedAt.toDate(),
            dueDate: doc.data().dueDate?.toDate(),
          }) as JudgeAssignment
      );

      callback(assignments);
    });
  },

  // Subscribe to project scores and comments
  subscribeToProjectScores(
    projectId: string,
    callback: (data: ProjectJudgingData) => void
  ) {
    const scoresRef = collection(db, 'projectScores');
    const q = query(
      scoresRef,
      where('projectId', '==', projectId),
      orderBy('createdAt', 'desc')
    );

    return onSnapshot(q, async (snapshot) => {
      const scores = snapshot.docs.map((doc) => {
        const data = doc.data();
        return {
          id: doc.id,
          ...data,
          createdAt: data.createdAt.toDate(),
          updatedAt: data.updatedAt.toDate(),
          // Preserve rubric fields if they exist
          rubricId: data.rubricId,
          rubricScores: data.rubricScores,
        } as ProjectScore & {
          rubricId?: string;
          rubricScores?: { questionId: string; pointsAwarded: number }[];
        };
      });

      // Calculate aggregate data
      const totalScores = scores.length;
      const averageScore =
        scores.length > 0
          ? scores.reduce((sum, score) => sum + score.score, 0) / totalScores
          : 0;

      // Get assignments for this project
      const assignmentsRef = collection(db, 'judgeAssignments');
      const assignmentsQuery = query(
        assignmentsRef,
        where('projectId', '==', projectId)
      );
      const assignmentsSnapshot = await getDocs(assignmentsQuery);
      const assignments = assignmentsSnapshot.docs.map(
        (doc) =>
          ({
            id: doc.id,
            ...doc.data(),
            assignedAt: doc.data().assignedAt.toDate(),
            dueDate: doc.data().dueDate?.toDate(),
          }) as JudgeAssignment
      );

      callback({
        projectId,
        totalScores,
        averageScore,
        scores,
        assignments,
      });
    });
  },

  // Submit a new score or update existing
  async submitProjectScore(
    projectId: string,
    judgeId: string,
    judgeName: string,
    score: number,
    comments: Omit<Comment, 'id' | 'createdAt' | 'updatedAt'>[],
    isAssigned: boolean = false
  ): Promise<void> {
    const scoresRef = collection(db, 'projectScores');
    const existingScoreQuery = query(
      scoresRef,
      where('projectId', '==', projectId),
      where('judgeId', '==', judgeId)
    );
    const existingScoreSnapshot = await getDocs(existingScoreQuery);

    const timestamp = new Date();
    const commentData = comments.map((comment) => ({
      ...comment,
      id: crypto.randomUUID(),
      createdAt: timestamp,
      updatedAt: timestamp,
    }));

    if (existingScoreSnapshot.empty) {
      // Create new score
      await addDoc(scoresRef, {
        projectId,
        judgeId,
        judgeName,
        score,
        comments: commentData,
        isAssigned,
        status: 'completed',
        createdAt: Timestamp.fromDate(timestamp),
        updatedAt: Timestamp.fromDate(timestamp),
      });
    } else {
      // Update existing score
      const scoreDoc = existingScoreSnapshot.docs[0];
      const existingComments = scoreDoc.data().comments || [];

      await updateDoc(scoreDoc.ref, {
        score,
        comments: [...existingComments, ...commentData],
        status: 'completed',
        updatedAt: Timestamp.fromDate(timestamp),
      });
    }

    // If this was an assigned project, update the assignment status
    if (isAssigned) {
      const assignmentsRef = collection(db, 'judgeAssignments');
      const assignmentQuery = query(
        assignmentsRef,
        where('projectId', '==', projectId),
        where('judgeId', '==', judgeId)
      );
      const assignmentSnapshot = await getDocs(assignmentQuery);

      if (!assignmentSnapshot.empty) {
        await updateDoc(assignmentSnapshot.docs[0].ref, {
          status: 'completed',
          updatedAt: Timestamp.fromDate(timestamp),
        });
      }
    }
  },

  // Add a comment to an existing score
  async addComment(
    scoreId: string,
    comment: Omit<Comment, 'id' | 'createdAt' | 'updatedAt'>
  ): Promise<void> {
    const scoreRef = doc(db, 'projectScores', scoreId);
    const scoreDoc = await getDoc(scoreRef);

    if (!scoreDoc.exists()) {
      throw new Error('Score not found');
    }

    const timestamp = new Date();
    const newComment = {
      ...comment,
      id: crypto.randomUUID(),
      createdAt: timestamp,
      updatedAt: timestamp,
    };

    const existingComments = scoreDoc.data().comments || [];
    await updateDoc(scoreRef, {
      comments: [...existingComments, newComment],
      updatedAt: Timestamp.fromDate(timestamp),
    });
  },

  // Get a specific project score for a judge
  async getProjectScore(
    projectId: string,
    judgeId: string
  ): Promise<ProjectScore | undefined> {
    const scoresRef = collection(db, 'projectScores');
    const q = query(
      scoresRef,
      where('projectId', '==', projectId),
      where('judgeId', '==', judgeId)
    );

    const snapshot = await getDocs(q);
    if (snapshot.empty) {
      return undefined;
    }

    const doc = snapshot.docs[0];
    return {
      id: doc.id,
      ...doc.data(),
      createdAt: doc.data().createdAt.toDate(),
      updatedAt: doc.data().updatedAt.toDate(),
    } as ProjectScore;
  },

  // Get judging statistics for a judge
  async getJudgeStats(judgeId: string): Promise<JudgingStats> {
    const assignmentsRef = collection(db, 'judgeAssignments');
    const assignmentsQuery = query(
      assignmentsRef,
      where('judgeId', '==', judgeId)
    );
    const assignmentsSnapshot = await getDocs(assignmentsQuery);

    const scoresRef = collection(db, 'projectScores');
    const scoresQuery = query(scoresRef, where('judgeId', '==', judgeId));
    const scoresSnapshot = await getDocs(scoresQuery);

    const scores = scoresSnapshot.docs.map((doc) => doc.data() as ProjectScore);

    return {
      totalAssigned: assignmentsSnapshot.size,
      completed: assignmentsSnapshot.docs.filter(
        (doc) => doc.data().status === 'completed'
      ).length,
      inProgress: assignmentsSnapshot.docs.filter(
        (doc) => doc.data().status === 'in_progress'
      ).length,
      pending: assignmentsSnapshot.docs.filter(
        (doc) => doc.data().status === 'pending'
      ).length,
      averageScore:
        scores.length > 0
          ? scores.reduce((sum, score) => sum + score.score, 0) / scores.length
          : 0,
    };
  },
};
